import { buildPublishPreview } from "../data/publish-preview";
import type { ScheduleCoverageRow } from "../data/schedule";
import { shiftWeek, weekDays, weekStartIso } from "../data/schedule-builder";
import type { ShiftTemplateRow } from "../data/schedule-templates";

/**
 * Console Schedule Wave 2, Slice B: the engine-free presentation model for the connected
 * create-and-publish wizard (Define -> Week -> Review -> Publish).
 *
 * Design source: qa/design/2026-08-15-console-schedule-connected-flow.html (Implement rules
 * 3, 4, 5 and frames 03, 04, 06, 07). Lifecycle semantics are unchanged from the
 * 2026-08-15 schedule delete/retire lock; this module adds no destructive verbs.
 *
 * Pure by construction: no React, no Supabase client, no RPC. Every function is data in,
 * data out, so the wizard's promises are node-testable and the Slice C surface can only
 * render what this module can prove.
 *
 * THE CENTRAL RULE (board Implement rule 4): "The count comes from the same expansion the
 * publish host will run, so preview and result can never disagree." `previewPublishWeek`
 * therefore mirrors `publish_shift_template`'s expansion exactly: clamp the window to the
 * template's active window, keep dates whose Postgres dow is in `weekdays`, and count the
 * candidates that do not already exist as slots. One slot per matching DATE, carrying
 * headcount as a column, which is what the RPC inserts.
 */

// --- Locked copy ------------------------------------------------------------------------
// The board fixes these strings; they are the contract, not suggestions.

/** Review headline when no week has been chosen yet (board frame 07). */
export const REVIEW_NO_WEEK_HEADLINE = "Creates 0 shifts · no week chosen yet";

/** Disabled-publish reason lines, verbatim from the frame 07 table. */
export const PUBLISH_BLOCK_REASONS = {
  "no-permission":
    "Schedule changes are unavailable right now. Check your access or try again.",
  "no-days": "This draft has no days selected",
  "no-week": "Pick a week before publishing",
  "nothing-new": "Nothing new to publish for this week",
  /**
   * Not in the board's four-row table. A retired pattern reaching Review would otherwise
   * leave a runnable-looking control that the RPC refuses ("Retired shift templates cannot
   * be published."), which is exactly the blue-but-inert defect the board bans. Wording
   * reuses the delete/retire lock's already-approved retire helper sentence verbatim
   * rather than inventing a fifth string.
   */
  retired: "Existing shifts stay. Pattern no longer offered for new publish.",
  /**
   * Also beyond the locked table, and deliberately vague because it fires on a status this
   * build does not recognize. Blocking the unknown is the safe default: a status added
   * later (archived, suspended) must not inherit a runnable publish control just because
   * nobody updated this file. Needs the same copy ruling as `retired`.
   */
  "not-publishable": "This pattern is not available for publishing",
  /**
   * Slice C addition (QA finding Q2/Q3): Review can be reached with a shape the publish
   * host has never stored - a hand-edited address before the first save, or browser
   * Back-edit-Forward past the Continue that would have written. Publishing would expand
   * the server's row, not the shape on screen, so the preview could not be honest and the
   * control must not run. Repair returns to Define, whose Continue writes.
   */
  unsaved: "Save the shift definition before publishing",
} as const satisfies Record<string, string>;

export type PublishBlockKind = keyof typeof PUBLISH_BLOCK_REASONS;

/** Draft exit toast (board frame 06): green states the fact, blue offers the next act. */
export const DRAFT_SAVED_PROOF = "Draft saved";
export const DRAFT_SAVED_BODY = "Schedule it when you are ready.";
export const DRAFT_RESUME_ACTION = "Schedule now";

/** Primary CTA into the wizard, from the hub and the Schedule empty state. */
export const SCHEDULE_SHIFT_CTA = "Schedule a shift";

// --- Dates ---------------------------------------------------------------------------------
// All date math is UTC-anchored on YYYY-MM-DD strings, matching schedule-builder.ts. The
// week anchor is deliberately NOT redefined here: weekStartIso is imported so the wizard and
// the Builder grid can never disagree about which seven days a week is.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !ISO_DATE.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  // "9999-99-99" passes the shape test but parses to an Invalid Date, whose toISOString
  // throws. Check the time value before formatting so this predicate can only ever answer,
  // never throw at its callers.
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }
  // Rejects impossible dates that still parse (2026-02-31 rolls forward to March 3).
  return parsed.toISOString().slice(0, 10) === value;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Labels are composed by hand rather than through Intl on purpose. The board locks the
 * exact shape ("Mon Aug 18", no comma), and Intl's en-US short format inserts one and may
 * repunctuate across ICU versions, which would silently rewrite a locked string.
 */

/**
 * "Sun Aug 16" - the weekday-and-day format the locked review string uses. Empty for a
 * date this module refuses, so a malformed value cannot become plausible-looking nonsense
 * ("Tue Feb 31") inside a sentence the operator is about to act on.
 */
export function formatDayLabel(dateIso: string): string {
  if (!isIsoDate(dateIso)) {
    return "";
  }
  return `${WEEKDAY_SHORT[dowOf(dateIso)] ?? ""} ${formatMonthDay(dateIso)}`.trim();
}

/** "Aug 16" - no weekday, for the "Week of" title. Empty for a refused date. */
export function formatMonthDay(dateIso: string): string {
  if (!isIsoDate(dateIso)) {
    return "";
  }
  const month = MONTH_SHORT[Number(dateIso.slice(5, 7)) - 1] ?? "";
  return `${month} ${Number(dateIso.slice(8, 10))}`.trim();
}

/**
 * Postgres dow (0 = Sunday .. 6 = Saturday), the convention `weekdays` already uses.
 * Returns -1 for a date this module refuses, which matches no weekday, so a bad date can
 * never accidentally select one.
 */
export function dowOf(dateIso: string): number {
  if (!isIsoDate(dateIso)) {
    return -1;
  }
  return new Date(`${dateIso}T00:00:00Z`).getUTCDay();
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * The weekday the shipped Builder anchors a week on, derived from `weekStartIso` rather
 * than assumed here. If that anchor ever moves, every label in this module follows it and
 * the wizard cannot start promising a week the Builder does not draw.
 */
const WEEK_ANCHOR_DOW = dowOf(weekStartIso("2026-01-01"));

// --- Week options (board frame 03: weeks are rows with names) --------------------------------

export interface WeekOption {
  /** Week start, on the shipped Builder anchor. Doubles as the option key. */
  readonly weekStart: string;
  readonly weekEnd: string;
  /** "This week" or "Week of Aug 23". */
  readonly title: string;
  /** "Sun Aug 23 to Sat Aug 29". */
  readonly rangeLabel: string;
  /** Present only where it says something true: "already underway", "Next week". */
  readonly note: string | null;
  readonly isCurrent: boolean;
  readonly isNext: boolean;
}

/**
 * The honest sentence the selected row carries (board callout 2: the Builder mapping is
 * admitted once, in small type). It can be stated plainly because the wizard week and the
 * Builder week are the same seven days by construction.
 */
export function builderWeekNote(weekStart: string): string | null {
  if (!isBuilderWeekStart(weekStart)) {
    return null;
  }
  const [start, end] = weekBounds(weekStart);
  // The month is named once when the week stays inside it ("Aug 16 to 22"), and twice when
  // the week straddles a boundary ("Aug 30 to Sep 5"), so the note never reads as one month.
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  const endLabel = sameMonth ? String(Number(end.slice(8, 10))) : formatMonthDay(end);
  return `Fills the Builder week ${formatMonthDay(start)} to ${endLabel}`;
}

function weekBounds(weekStart: string): readonly [string, string] {
  const days = weekDays(weekStart);
  return [days[0]!, days[6]!];
}

/**
 * Is this date actually the start of a Builder week?
 *
 * `weekDays` returns seven days from whatever date it is handed, so a mid-week date would
 * otherwise produce a confident sentence about a seven-day span the Builder never draws
 * (and a publish window straddling two of its weeks). Deep links, resumed drafts, and
 * callers that pass "today" where a chosen week start belongs all reach these helpers, so
 * every entry point validates the anchor rather than trusting its caller.
 */
export function isBuilderWeekStart(weekStart: unknown): weekStart is string {
  return isIsoDate(weekStart) && weekStartIso(weekStart) === weekStart;
}

/** The board shows four rows; three times that is generous and still a bounded list. */
export const MAX_WEEK_OPTIONS = 12;
const DEFAULT_WEEK_OPTIONS = 4;

/**
 * Bound the row count. Without a ceiling a caller-supplied value (a deep link, a stored
 * preference, a "show more weeks" control) could freeze the main thread building tens of
 * thousands of rows, and an infinite value would run the loop until the Date range gave
 * out and threw. A picker's length is never worth either.
 */
function clampWeekOptionCount(requested: number | undefined): number {
  if (requested === undefined) {
    return DEFAULT_WEEK_OPTIONS;
  }
  if (!Number.isFinite(requested)) {
    return 0;
  }
  return Math.min(MAX_WEEK_OPTIONS, Math.max(0, Math.trunc(requested)));
}

/**
 * Named week rows starting at the week containing `todayIso`. One week at a time, because
 * that is what the publish host does per week (board Call 2); the caller offers "Schedule
 * another week" to loop rather than inventing multi-week publish.
 */
export function buildWeekOptions(input: {
  readonly todayIso: string;
  /** How many consecutive weeks to offer, starting with the current one. Clamped. */
  readonly count?: number;
}): readonly WeekOption[] {
  if (!isIsoDate(input.todayIso)) {
    return [];
  }
  const count = clampWeekOptionCount(input.count);
  const currentWeekStart = weekStartIso(input.todayIso);
  const options: WeekOption[] = [];
  for (let index = 0; index < count; index += 1) {
    const weekStart = shiftWeek(currentWeekStart, index);
    const [start, end] = weekBounds(weekStart);
    const isCurrent = index === 0;
    const isNext = index === 1;
    options.push({
      weekStart: start,
      weekEnd: end,
      title: isCurrent ? "This week" : `Week of ${formatMonthDay(start)}`,
      rangeLabel: `${formatDayLabel(start)} to ${formatDayLabel(end)}`,
      note: isCurrent ? "already underway" : isNext ? "Next week" : null,
      isCurrent,
      isNext,
    });
  }
  return options;
}

// --- Pattern facts ----------------------------------------------------------------------------

/** Minutes past midnight for a "HH:MM" or "HH:MM:SS" local start time; null when unusable. */
export function startMinutes(localStartTime: string): number | null {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(localStartTime);
  if (match === null) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
}

function clockLabel(minutesPastMidnight: number): string {
  const wrapped = ((minutesPastMidnight % 1440) + 1440) % 1440;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export interface ShiftHours {
  /** "22:00 to 06:00". */
  readonly label: string;
  /** True when the shift ends on the following day (board: overnight note when end < start). */
  readonly overnight: boolean;
}

/** Plain hours for the pattern, or null when the template carries no usable start time. */
export function shiftHours(template: {
  readonly localStartTime: string;
  readonly durationMinutes: number;
}): ShiftHours | null {
  const start = startMinutes(template.localStartTime);
  if (start === null || !Number.isFinite(template.durationMinutes) || template.durationMinutes <= 0) {
    return null;
  }
  const end = start + template.durationMinutes;
  return { label: `${clockLabel(start)} to ${clockLabel(end)}`, overnight: end > 1440 };
}

/**
 * Plain-calendar day summary (board Implement rule 4).
 *
 * All seven days read as a span ("Every night, Sun to Sat"); a subset names its days
 * ("Every Fri and Sat"). The board's example says "night" because its example pattern is
 * overnight, so the noun follows the actual hours rather than hard-coding a claim that a
 * 14:00 shift happens at night.
 */
export function daysSummary(input: {
  readonly weekdays: readonly number[];
  readonly overnight: boolean;
}): string | null {
  const unique = [...new Set(input.weekdays.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))].sort(
    (a, b) => a - b,
  );
  if (unique.length === 0) {
    return null;
  }
  const noun = input.overnight ? "night" : "day";
  if (unique.length === 7) {
    // Named on the shipped week anchor, so the span matches the week the Builder draws.
    const first = WEEKDAY_SHORT[WEEK_ANCHOR_DOW]!;
    const last = WEEKDAY_SHORT[(WEEK_ANCHOR_DOW + 6) % 7]!;
    return `Every ${noun}, ${first} to ${last}`;
  }
  const names = unique.map((day) => WEEKDAY_SHORT[day]!);
  if (names.length === 1) {
    return `Every ${names[0]}`;
  }
  if (names.length === 2) {
    return `Every ${names[0]} and ${names[1]}`;
  }
  return `Every ${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

// --- Publish expansion preview -------------------------------------------------------------

/**
 * A mirror of `publish_shift_template`'s expansion, in pure data.
 *
 * The RPC clamps the window to [active_from, active_until], keeps dates whose dow is in
 * `weekdays`, and inserts ONE slot per surviving date carrying `headcount_required`. So
 * `newSlotCount` here is the RPC's `created_slot_count` and `alreadyPublishedCount` is its
 * `existing_slot_count`. Nothing else may be presented as "the count".
 */
export interface PublishPreview {
  /** Every matching date in the week, already published or not, in calendar order. */
  readonly candidateDates: readonly string[];
  /** Dates a publish would create now. */
  readonly newDates: readonly string[];
  /** Dates that already carry a slot for this pattern. */
  readonly alreadyPublishedDates: readonly string[];
  /** The RPC's created_slot_count for this window. */
  readonly newSlotCount: number;
  readonly alreadyPublishedCount: number;
  /**
   * Officers needed across the new slots (slots x headcount). Deliberately NOT the headline
   * count: the proof chip shows the server's slot count, and the two must never disagree.
   */
  readonly officerShiftCount: number;
  /** True when the template's active window trimmed the chosen week. */
  readonly clampedByActiveWindow: boolean;
  /**
   * How many of the already-carrying dates are CANCELLED slots that still block
   * re-creation (the RPC's idempotency check ignores status; board frame 05: "a cancelled
   * time still blocks republish of that same time"). Only the coverage-backed
   * `previewFromHostExpansion` can know this; the date-only expansion reports 0.
   */
  readonly cancelledBlockingCount: number;
}

export const EMPTY_PUBLISH_PREVIEW: PublishPreview = {
  candidateDates: [],
  newDates: [],
  alreadyPublishedDates: [],
  newSlotCount: 0,
  alreadyPublishedCount: 0,
  officerShiftCount: 0,
  clampedByActiveWindow: false,
  cancelledBlockingCount: 0,
};

export interface PublishPreviewInput {
  readonly template: Pick<
    ShiftTemplateRow,
    "weekdays" | "headcountRequired" | "activeFrom" | "activeUntil"
  >;
  /** Week start on the shipped Builder anchor; null before the operator picks one. */
  readonly weekStart: string | null;
  /** Slot dates this pattern already has in that week (from the coverage read). */
  readonly existingSlotDates?: readonly string[];
}

export function previewPublishWeek(input: PublishPreviewInput): PublishPreview {
  const { weekStart, template } = input;
  // A mid-week date would expand into seven days the Builder does not draw, so it is
  // refused exactly like a malformed one rather than quietly previewed.
  if (!isBuilderWeekStart(weekStart)) {
    return EMPTY_PUBLISH_PREVIEW;
  }
  const days = weekDays(weekStart);
  const activeFrom = isIsoDate(template.activeFrom) ? template.activeFrom : null;
  const activeUntil = isIsoDate(template.activeUntil) ? template.activeUntil : null;
  const wanted = new Set(
    template.weekdays.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
  );
  const existing = new Set(input.existingSlotDates ?? []);

  const candidateDates: string[] = [];
  let clamped = false;
  for (const date of days) {
    // The weekday test comes FIRST on purpose. A Monday the pattern never wanted is not a
    // day the active window "skipped", and reporting it as one would put a false sentence
    // on the surface the board calls the honest preview.
    if (!wanted.has(dowOf(date))) {
      continue;
    }
    // greatest(window_start, active_from) / least(window_end, active_until), per date.
    if (activeFrom !== null && date < activeFrom) {
      clamped = true;
      continue;
    }
    if (activeUntil !== null && date > activeUntil) {
      clamped = true;
      continue;
    }
    candidateDates.push(date);
  }

  const newDates = candidateDates.filter((date) => !existing.has(date));
  const alreadyPublishedDates = candidateDates.filter((date) => existing.has(date));
  const headcount =
    Number.isFinite(template.headcountRequired) && template.headcountRequired > 0
      ? Math.trunc(template.headcountRequired)
      : 0;
  return {
    candidateDates,
    newDates,
    alreadyPublishedDates,
    newSlotCount: newDates.length,
    alreadyPublishedCount: alreadyPublishedDates.length,
    officerShiftCount: newDates.length * headcount,
    clampedByActiveWindow: clamped,
    // Slot dates alone cannot say whether the blocking slot is cancelled.
    cancelledBlockingCount: 0,
  };
}

/**
 * The connected wizard's preview, from the SAME projection the Builder's publish review
 * gate already trusts (`data/publish-preview.ts`), which mirrors the shipped RPC exactly:
 * the idempotency match is on (date, starts_at) and ignores slot status, so an edited
 * start time adds a second slot on the same date and a cancelled slot still blocks
 * re-creation. `previewPublishWeek` above models the date-level expansion for callers that
 * only hold slot DATES; when the actual coverage rows are in hand, this is the stricter
 * truth and the wizard's Review step uses it (Slice C: host expansion wins on counts).
 *
 * `newSlotCount` here is the RPC's created_slot_count for the week. One slot per created
 * (date, starts_at), carrying headcount as a column, never nights times officers.
 */
export function previewFromHostExpansion(
  template: ShiftTemplateRow,
  coverage: readonly ScheduleCoverageRow[],
  weekStart: string | null,
): PublishPreview {
  if (!isBuilderWeekStart(weekStart)) {
    return EMPTY_PUBLISH_PREVIEW;
  }
  const [windowStart, windowEnd] = weekBounds(weekStart);
  const faithful = buildPublishPreview(template, coverage, windowStart, windowEnd);
  const newDates: string[] = [];
  const alreadyPublishedDates: string[] = [];
  for (const row of faithful.rows) {
    if (row.verdict === "create") {
      newDates.push(row.date);
    } else {
      alreadyPublishedDates.push(row.date);
    }
  }
  // Clamped means the active window removed a date the pattern actually WANTED, the same
  // wanted-first rule `previewPublishWeek` applies: a Monday the pattern never repeats on
  // is not a day the window "skipped".
  const wanted = new Set(
    template.weekdays.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
  );
  const inEffectiveWindow = (date: string): boolean =>
    faithful.effectiveStart !== null &&
    faithful.effectiveEnd !== null &&
    date >= faithful.effectiveStart &&
    date <= faithful.effectiveEnd;
  const clampedByActiveWindow = weekDays(weekStart).some(
    (date) => wanted.has(dowOf(date)) && !inEffectiveWindow(date),
  );
  const headcount =
    Number.isFinite(template.headcountRequired) && template.headcountRequired > 0
      ? Math.trunc(template.headcountRequired)
      : 0;
  return {
    candidateDates: faithful.rows.map((row) => row.date),
    newDates,
    alreadyPublishedDates,
    newSlotCount: newDates.length,
    alreadyPublishedCount: alreadyPublishedDates.length,
    officerShiftCount: newDates.length * headcount,
    clampedByActiveWindow,
    cancelledBlockingCount: faithful.summary.cancelledBlockingCount,
  };
}

// --- Disabled publish (board Implement rule 3: one reason, never stacked) ----------------------

export interface PublishBlock {
  readonly kind: PublishBlockKind;
  readonly reason: string;
  /**
   * The ghost button that repairs the block, where one exists (board: "a ghost button that
   * fixes the block whenever one exists"). Null when the fix is not a wizard step.
   */
  readonly repairLabel: string | null;
  /** Wizard step the repair goes to, so the stepper can ring the same gap. */
  readonly repairStep: WizardStep | null;
}

export type WizardStep = "define" | "week" | "review" | "published";

export const WIZARD_STEPS: readonly WizardStep[] = ["define", "week", "review", "published"];

/**
 * Priority order. Permission speaks first because nothing downstream can run without it;
 * then the blocks resolve in wizard order, so the reason always points at the earliest
 * thing the operator must fix. Reasons never stack: exactly one speaks.
 */
const BLOCK_PRIORITY: readonly PublishBlockKind[] = [
  "no-permission",
  "retired",
  "not-publishable",
  // Before the shape blocks: an unwritten shape makes every downstream fact unreliable.
  "unsaved",
  "no-days",
  "no-week",
  "nothing-new",
];

/** The two statuses `publish_shift_template` will act on. Everything else is blocked. */
const PUBLISHABLE_TEMPLATE_STATUSES: ReadonlySet<string> = new Set(["draft", "published"]);

const BLOCK_REPAIR: Readonly<
  Record<PublishBlockKind, { label: string | null; step: WizardStep | null }>
> = {
  "no-permission": { label: null, step: null },
  retired: { label: null, step: null },
  "not-publishable": { label: null, step: null },
  unsaved: { label: "Edit shift", step: "define" },
  "no-days": { label: "Edit shift", step: "define" },
  "no-week": { label: "Choose week", step: "week" },
  "nothing-new": { label: "Choose week", step: "week" },
};

export interface PublishReadinessInput {
  readonly canManageSchedule: boolean;
  readonly templateStatus: string;
  readonly weekdayCount: number;
  readonly weekStart: string | null;
  readonly preview: PublishPreview;
  /**
   * False when the shape on screen has never been written, or has been edited since the
   * last write (QA Q2/Q3). Optional so date-only callers that cannot know keep working;
   * omitted means saved, because only the wizard's own state can assert otherwise.
   */
  readonly draftSaved?: boolean;
}

/**
 * The single block that speaks, or null when publish can genuinely run.
 *
 * A null return is the ONLY case where the surface may render a primary-styled publish
 * control: a blue control that cannot run is a review-blocking defect on this board.
 *
 * DISPLAY GATE ONLY. `canManageSchedule` decides what the operator is shown, nothing more.
 * `publish_shift_template` enforces `schedule.manage` server-side and refuses regardless of
 * what this returns, so a caller must never treat a null here as authorization.
 */
export function resolvePublishBlock(input: PublishReadinessInput): PublishBlock | null {
  const found: PublishBlockKind[] = [];
  if (!input.canManageSchedule) {
    found.push("no-permission");
  }
  if (input.draftSaved === false) {
    found.push("unsaved");
  }
  // Allow the known-publishable statuses rather than denying today's known-bad one: a
  // status added later (archived, suspended) must not inherit a runnable publish control
  // just because this file was not updated with it.
  const status = typeof input.templateStatus === "string" ? input.templateStatus.trim().toLowerCase() : "";
  if (!PUBLISHABLE_TEMPLATE_STATUSES.has(status)) {
    found.push(status === "retired" ? "retired" : "not-publishable");
  }
  if (!Number.isFinite(input.weekdayCount) || input.weekdayCount <= 0) {
    found.push("no-days");
  }
  if (!isBuilderWeekStart(input.weekStart)) {
    // Covers null and anything that is not a real Builder week start; in both cases the
    // operator's next move is the same, so the locked "Pick a week" line is the honest one.
    found.push("no-week");
  } else if (!Number.isFinite(input.preview.newSlotCount) || input.preview.newSlotCount === 0) {
    // Distinct from "no days": the week is chosen and every matching date already exists,
    // or the active window trimmed them all away.
    found.push("nothing-new");
  }
  const kind = BLOCK_PRIORITY.find((candidate) => found.includes(candidate));
  if (kind === undefined) {
    return null;
  }
  const repair = BLOCK_REPAIR[kind];
  return {
    kind,
    reason: PUBLISH_BLOCK_REASONS[kind],
    repairLabel: repair.label,
    repairStep: repair.step,
  };
}

/** True only when a primary-styled publish control is honest to render. */
export function canRenderPrimaryPublish(input: PublishReadinessInput): boolean {
  return resolvePublishBlock(input) === null;
}

// --- Review preview copy (board Implement rule 4) -----------------------------------------------

export interface ReviewPreview {
  /** The locked headline sentence. */
  readonly headline: string;
  /** Supporting fact lines, in render order. Never contradicts the headline. */
  readonly facts: readonly string[];
  /** Nothing has happened yet; the surface states this under the week ghost. */
  readonly footnote: string;
}

export const REVIEW_FOOTNOTE = "Nothing is on the calendar until you publish.";

/** Long enough for any real pattern name, short enough to stay one headline line. */
export const HEADLINE_NAME_MAX = 60;

/**
 * Make an account-supplied name safe to seat inside the locked headline.
 *
 * The headline joins its parts with the middle dot, so a name containing that separator
 * (or a newline) could otherwise forge extra fact segments in the one sentence the board
 * treats as the authoritative pre-publish claim, including a second, contradictory shift
 * count. The name is data, so it is neutralized rather than trusted: separators dropped,
 * whitespace collapsed, length bounded.
 */
function headlineSegment(name: string): string {
  const cleaned = (typeof name === "string" ? name : "")
    .replace(/·/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned.length === 0) {
    return "this pattern";
  }
  return cleaned.length <= HEADLINE_NAME_MAX
    ? cleaned
    : `${cleaned.slice(0, HEADLINE_NAME_MAX - 1).trimEnd()}…`;
}

export interface ReviewPreviewInput {
  readonly template: Pick<
    ShiftTemplateRow,
    | "name"
    | "weekdays"
    | "headcountRequired"
    | "localStartTime"
    | "durationMinutes"
    | "openFillMode"
  >;
  readonly weekStart: string | null;
  readonly preview: PublishPreview;
  /** Optional site and route names for the "where" fact line. */
  readonly siteName?: string | null;
  readonly routeName?: string | null;
}

/**
 * The review card's copy.
 *
 * Headline format (locked): `Creates {n} shifts · {start} to {end} · {pattern} · {k} officers`,
 * with the no-week case falling back to the locked zero string. `{n}` is the publish host's
 * created-slot count, so the proof chip that follows can only ever repeat it.
 */
export function buildReviewPreview(input: ReviewPreviewInput): ReviewPreview {
  const { template, weekStart, preview } = input;
  const hours = shiftHours(template);
  const overnight = hours?.overnight ?? false;
  const days = daysSummary({ weekdays: template.weekdays, overnight });
  const headcount =
    Number.isFinite(template.headcountRequired) && template.headcountRequired > 0
      ? Math.trunc(template.headcountRequired)
      : 0;

  const facts: string[] = [];
  if (days !== null && hours !== null) {
    facts.push(`${days} · ${hours.label}${overnight ? ", overnight" : ""}`);
  } else if (days !== null) {
    facts.push(days);
  } else if (hours !== null) {
    facts.push(hours.label);
  }
  const where = [input.siteName, input.routeName].filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
  if (where.length > 0) {
    facts.push(where.join(" · "));
  }
  if (headcount > 0) {
    const officers = `${headcount} ${headcount === 1 ? "officer" : "officers"}`;
    if (preview.newSlotCount > 0) {
      // The headline count is the host's slot count (Slice B ruling, not reopened). The
      // board's frame also wanted the officers-across-the-week figure, so it rides here
      // as SEATS, separately and never as the shift count: 7 nights of 2 is 7 shifts and
      // 14 seats, and the two must stay distinguishable at a glance.
      const shifts = `${preview.newSlotCount} ${preview.newSlotCount === 1 ? "shift" : "shifts"} total`;
      const seats =
        headcount > 1 && Number.isFinite(preview.officerShiftCount) && preview.officerShiftCount > 0
          ? ` · ${preview.officerShiftCount} seats to fill across the week`
          : "";
      facts.push(`${officers} per shift, ${shifts}${seats}`);
    } else {
      facts.push(`${officers} per shift`);
    }
  }
  if (template.openFillMode === "request_approval") {
    facts.push("Officers can request these shifts in Guard. A supervisor approves requests.");
  } else {
    facts.push("Officers can take these shifts in Guard as open shifts.");
  }
  if (preview.alreadyPublishedCount > 0) {
    const already = preview.alreadyPublishedCount;
    facts.push(
      already === 1
        ? "1 shift is already published for this week and stays as it is."
        : `${already} shifts are already published for this week and stay as they are.`,
    );
  }
  if (preview.cancelledBlockingCount > 0) {
    const cancelled = preview.cancelledBlockingCount;
    facts.push(
      cancelled === 1
        ? "1 of those is a cancelled slot. A cancelled time still blocks republish of the same time."
        : `${cancelled} of those are cancelled slots. A cancelled time still blocks republish of the same time.`,
    );
  }
  if (preview.clampedByActiveWindow) {
    facts.push("Some days in this week fall outside the pattern's active dates and are skipped.");
  }

  if (!isBuilderWeekStart(weekStart)) {
    return { headline: REVIEW_NO_WEEK_HEADLINE, facts, footnote: REVIEW_FOOTNOTE };
  }
  const [start, end] = weekBounds(weekStart);
  const count = Number.isFinite(preview.newSlotCount) ? Math.max(0, Math.trunc(preview.newSlotCount)) : 0;
  const shifts = `${count} ${count === 1 ? "shift" : "shifts"}`;
  const parts = [
    `Creates ${shifts}`,
    `${formatDayLabel(start)} to ${formatDayLabel(end)}`,
    headlineSegment(template.name),
  ];
  if (headcount > 0) {
    parts.push(`${headcount} ${headcount === 1 ? "officer" : "officers"}`);
  }
  return { headline: parts.join(" · "), facts, footnote: REVIEW_FOOTNOTE };
}

// --- Proof (board Implement rule 8: the count on the chip is the server's count) ----------------

/**
 * Success copy built from the publish receipt, never from the preview. The board forbids
 * optimistic green, so this takes the RPC's own created count as its only source.
 */
export function buildPublishedProof(receipt: { readonly createdSlotCount: number }): string {
  const raw = receipt.createdSlotCount;
  if (!Number.isFinite(raw)) {
    // The publish succeeded, so the chip must not retract that; but an unusable count
    // cannot be rendered as a number, and clamping it to 0 would assert that nothing was
    // created. State the fact without the figure instead. Needs the same copy ruling as
    // the extra block reasons.
    return "Published · the new shifts are on the calendar";
  }
  const count = Math.max(0, Math.trunc(raw));
  return `Published · ${count} ${count === 1 ? "shift is" : "shifts are"} on the calendar`;
}

// --- Draft handoff (board Implement rule 5: Save draft is an exit, never a dead end) -------------

/**
 * What Save draft hands the hub, and what Schedule now hands back to the wizard. Both
 * directions resume at Choose week with the pattern loaded, so a draft is never finished
 * by force in Builder.
 */
export interface DraftHandoff {
  readonly templateId: string;
  readonly patternName: string;
  /** Every resume path re-enters at the week step. */
  readonly resumeStep: Extract<WizardStep, "week">;
}

export function buildDraftHandoff(template: Pick<ShiftTemplateRow, "templateId" | "name">): DraftHandoff {
  return {
    templateId: template.templateId,
    patternName: template.name.trim().length > 0 ? template.name.trim() : "Untitled pattern",
    resumeStep: "week",
  };
}

export interface DraftSavedToast {
  readonly proof: string;
  readonly body: string;
  readonly actionLabel: string;
  readonly handoff: DraftHandoff;
}

export function buildDraftSavedToast(
  template: Pick<ShiftTemplateRow, "templateId" | "name">,
): DraftSavedToast {
  return {
    proof: DRAFT_SAVED_PROOF,
    body: DRAFT_SAVED_BODY,
    actionLabel: DRAFT_RESUME_ACTION,
    handoff: buildDraftHandoff(template),
  };
}

/**
 * Resume cue loudness (board Call 4): a draft saved in this session resumes loudly, older
 * drafts resume quietly, so the hub never carries two primary controls. Freshness is
 * session membership, deliberately not a timestamp rule.
 */
export type DraftResumeTone = "primary" | "quiet";

export function draftResumeTone(input: {
  readonly templateId: string;
  readonly savedThisSession: ReadonlySet<string>;
}): DraftResumeTone {
  return input.savedThisSession.has(input.templateId) ? "primary" : "quiet";
}

/**
 * Row-level resume cue for the Templates list. Drafts get "Schedule now"; a published
 * pattern is not resumable and gets no cue (its menu offers Schedule this pattern).
 */
export function draftResumeCue(input: {
  readonly template: Pick<ShiftTemplateRow, "templateId" | "name" | "status">;
  readonly savedThisSession: ReadonlySet<string>;
}): { readonly label: string; readonly tone: DraftResumeTone; readonly handoff: DraftHandoff } | null {
  if (input.template.status !== "draft") {
    return null;
  }
  return {
    label: DRAFT_RESUME_ACTION,
    tone: draftResumeTone({
      templateId: input.template.templateId,
      savedThisSession: input.savedThisSession,
    }),
    handoff: buildDraftHandoff(input.template),
  };
}
